<template>
  <div>
    <section id="home">
      <div class="blob blob-top-right bg-red"></div>
      <div class="blob blob-bottom-left bg-yellow"></div>
      <div class="container">
        <div class="p-grid">
          <div class="p-col">
            <div class="h-100 flex-column items-center justify-center">
              <img :src="logo" alt="CodeSnippet" />
              <h1 class="my-2">Créez, codez et partagez !</h1>
              <p class="bigger-text">
                CodeSnippet est le nouvel outil pédagogique et collaboratif pour
                apprendre les bases du développement web. En créant un projet
                Snippet, vous pouvez travailler sur du HTML, CSS et JavaScript
                avec vos professeurs, vos amis et d'autres étudiants.
              </p>
              <div class="flex mt-3 flex-wrap cta-row">
                <router-link to="#more" title="En apprendre plus sur le projet">
                  <Button label="Découvrir" class="mr-2 bg-purple" />
                </router-link>
                <router-link
                  :to="authenticated === true ? { name: 'Dashboard' } : { name: 'Authentication' }"
                  title="se créer un compte"
                >
                  <Button :label="authenticated === true ? 'Dashboard' : 'S\'inscrire'" />
                </router-link>
              </div>
            </div>
          </div>
          <div class="p-col pr-5 h-100 img-col">
            <div class="w-100 flex items-center justify-end">
              <img
                :src="this.$store.state.dark ? lpImgDark : lpImgLight"
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="overflow-hidden" id="more">
      <div class="blob blob-gigantic blob-bottom-right bg-blue"></div>
      <div class="container">
        <div class="p-grid p-jc-center">
          <div class="p-col-12 p-md-5">
            <div class="bloc-interest">
              <v-mdi size="54" name="mdi-code-tags"></v-mdi>
              <h2>Indentation & compilation</h2>
              <p>
                Notre éditeur de code se charge d'indenter et colorer votre code pour améliorer sa lisibilité !
                Une prévisualisation est également disponible à chaque nouvelle modification.
              </p>
            </div>
          </div>
          <div class="p-col-12 p-md-5">
            <div class="bloc-interest">
              <v-mdi size="54" name="mdi-school"></v-mdi>
              <h2>Mode enseignant</h2>
              <p>
                Vous pouvez regrouper vos élèves dans un projet et les faire travailler par petits groupes sur un code modèle,
                tout en gérant leurs accès en lecture et écriture !
              </p>
            </div>
          </div>
        </div>
        <div class="p-grid p-jc-center pt-2">
          <div class="p-col-12 p-md-5">
            <div class="bloc-interest">
              <v-mdi size="54" name="mdi-account-multiple"></v-mdi>
              <h2>Travail collaboratif</h2>
              <p>
                Notre éditeur de code intégré vous permet de voir les modifications de vos collaborateurs développeurs en direct,
                et cela pour chaque fichier et groupe du projet.
              </p>
            </div>
          </div>
          <div class="p-col-12 p-md-5">
            <div class="bloc-interest">
              <v-mdi size="54" name="mdi-timer-outline"></v-mdi>
              <h2>Verrouillage de projets</h2>
              <p>
                En donnant une date de début et/ou une date de fin à votre projet en mode examen, vous verrouillez l'accès au projet aux membres. Utile pour un projet noté !
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-variant">
      <div class="container">
        <h1 class="big-title">Comment ça marche ?</h1>
        <div class="p-grid">
          <div class="p-col-12 p-md-3 p-d-flex p-flex-column p-ai-center">
            <Tag value="Créer un nouveau projet" class="full-tag border-blue"></Tag>
            <v-mdi class="my-1" size="30" name="mdi-arrow-down"></v-mdi>
            <Tag value="Ajouter des particpants" class="full-tag border-red"></Tag>
            <v-mdi class="my-1" size="30" name="mdi-arrow-down"></v-mdi>
            <Tag value="Développer" class="full-tag border-purple"></Tag>
            <v-mdi class="my-1" size="30" name="mdi-arrow-down"></v-mdi>
            <Tag value="Prévisualiser le rendu" class="full-tag border-green"></Tag>
          </div>
          <div class="p-col">
            <p>
              Dans un projet CodeSnippet, vous trouverez trois fichiers, chacun écrit dans un langage différent.
              Voici la liste ces <b>trois fichiers</b>, pour mieux comprendre la structure d'un projet CodeSnippet :
            </p>
            <div class="p-d-flex p-ai-center mt-3 chip-block">
              <Chip label="HTML" class="custom-chip mr-2 text-black" />
              <p>
                Langage de balises qui structure et donne du sens à une page web.
                Ce langage sert notamment à distinguer les paragraphes, tableaux, vidéos, images.
              </p>
            </div>
            <div class="p-d-flex p-ai-center mt-3 chip-block">
              <Chip label="CSS" class="custom-chip mr-2 text-black" />
              <p>
                Langage de règles de style qui permet de mettre en forme le contenu HTML et de le styliser.
                On peut y définir les couleurs du site, la taille des images, etc.
              </p>
            </div>
            <div class="p-d-flex p-ai-center mt-3 chip-block">
              <Chip label="JavaScript" class="custom-chip mr-2 text-black" />
              <p>
                Langage de programmation qui permet de rendre le contenu d'une page web dynamique.
                Il sert, par exemple, aux formulaires, boutons, etc.
              </p>
            </div>
            <p class="mt-3">
              Une fois ces fichiers assemblés, ceux-ci vous permettront de réaliser des sites web simples mais efficaces !
              Ces trois fichiers seront uniques pour chaque groupe d'utilisateurs dans un projet, et notre outil de prévisualisation vous
              permettra de voir en direct le rendu de votre code.
            </p>
            <p class="mt-3">
              Pour en apprendre plus sur le développement web, vous pouvez consulter
              <a class="link" title="Documentation pour en apprendre plus sur le développement web" href="https://developer.mozilla.org/fr/docs/Learn/Getting_started_with_the_web" >
                le guide "Commencer avec le Web" par MDN Web Docs
              </a>
            </p>
          </div>
        </div>
        <div class="p-d-flex p-jc-center">
           <router-link
                  :to="{ name: 'Guide' }"
                  title="Accéder au guide d'utilisation du site"
                >
              <Button label="Lire le guide" class="mt-5" />
            </router-link>
        </div>
      </div>
    </section>

    <section id="team" class="overflow-hidden">
      <div class="blob blob-gigantic blob-bottom-left bg-green"></div>
      <div class="container">
        <h1 class="big-title">La fine équipe</h1>
		    <div class="p-grid p-jc-center">
          		<div class="p-col-12 p-md-5 p-d-flex p-flex-column">
        			<h2>La partie BACK</h2>
					<div class="p-d-flex mb-4 p-ai-center">
						<img :src="team.gregg" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Gregg SANCHEZ</p>
							<p>Responsable partie back</p>
							<small>Folle à chiens</small>
						</div>
					</div>
					<div class="p-d-flex mb-4 p-ai-center self-center">
						<img :src="team.elric" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Elric GUMBAU</p>
							<p>Partie enseignement & gestion des groupes</p>
							<small>Joue à Visual Studio Code</small>
						</div>
					</div>
					<div class="p-d-flex mb-4 p-ai-center">
						<img :src="team.tim" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Tim GIMENEZ</p>
							<p>Partie authentification & gestion des projets</p>
							<small>A survécu à un kidnapping</small>
						</div>
					</div>
					<div class="p-d-flex mb-4 p-ai-center self-center">
						<img :src="team.antoine" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Antoine MOUSSET</p>
							<p>Partie administration et invitations</p>
							<small>Pacsé à Florian</small>
						</div>
					</div>
					<div class="p-d-flex mb-4 p-ai-center">
						<img :src="team.cedric" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Cédric COINDARD--LAGRESLE</p>
							<p>Dashboard administration & guide d'utilisation</p>
							<small>Le cadet du projet</small>
						</div>
					</div>
				</div>
          		<div class="p-col-12 p-md-5 p-d-flex p-flex-column">
        			<h2>La partie FRONT</h2>
					<div class="p-d-flex mb-4 p-ai-center">
						<img :src="team.aimee" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Aimee RITLENG</p>
							<p>Responsable partie front</p>
							<small>Folle à lier</small>
						</div>
					</div>
					<div class="p-d-flex mb-4 p-ai-center self-center">
						<img :src="team.jb" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Jean-Baptiste DONIN</p>
							<p>Création de projet, code en simultané & gestion des groupes</p>
							<small>Mastico officiel</small>
						</div>
					</div>
					<div class="p-d-flex mb-4 p-ai-center">
						<img :src="team.anael" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Anaël BONNAFOUS</p>
							<p>Authentification, gestion des projets & code en simultané</p>
							<small>Possède deux furets et une copine (dans cet ordre)</small>
						</div>
					</div>
					<div class="p-d-flex mb-4 p-ai-center self-center">
						<img :src="team.luca" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Luca PILLONI</p>
							<p>Partie administration & édition de code</p>
							<small>Le doyen du projet</small>
						</div>
					</div>
          <!--
          <div class="p-d-flex mb-4 p-ai-center">
						<img :src="team.gregg" alt="" class="rounded-full bg-purple mr-2" width="120" height="120" />
						<div>
							<p class="font-bold">Snippy</p>
							<p>Mascotte CodeSnippet</p>
							<small>Sert surtout de soutien mental</small>
						</div>
					</div>
          -->
				</div>
			</div>
		</div>
	</section>
  </div>
</template>
<script>
import lpImgLight from "../assets/img/landing-page/lp-img-light.png";
import lpImgDark from "../assets/img/landing-page/lp-img-dark.png";
import logo from "~/assets/logo.png";

// Avatars
import avatar_aimee from "~/assets/img/landing-page/Aimee.png";
import avatar_gregg from "~/assets/img/landing-page/Gregg.png";
import avatar_elric from "~/assets/img/landing-page/Elric.png";
import avatar_tim from "~/assets/img/landing-page/Tim.png";
import avatar_anael from "~/assets/img/landing-page/Anael.png";
import avatar_cedric from "~/assets/img/landing-page/Cedric.png";
import avatar_antoine from "~/assets/img/landing-page/Antoine.png";
import avatar_luca from "~/assets/img/landing-page/Luca.png";
import avatar_jb from "~/assets/img/landing-page/JB.png";

import { mapGetters } from 'vuex';

export default {
  data: () => ({
    retrieved: false,
    windowSize: 0,
    logo: logo,
    lpImgDark: lpImgDark,
    lpImgLight: lpImgLight,
    team: {
      aimee : avatar_aimee,
      gregg : avatar_gregg,
      elric : avatar_elric,
      tim : avatar_tim,
      anael : avatar_anael,
      cedric : avatar_cedric,
      antoine : avatar_antoine,
      luca : avatar_luca,
      jb : avatar_jb,
    },
  }),
  computed: {
    ...mapGetters({
      authenticated: 'auth/authenticated',
    }),
  },
  methods: {
    resizeHandler() {
      this.windowSize = window.innerWidth;
    },
  },
  mounted() {
    this.resizeHandler();
    window.addEventListener("resize", this.resizeHandler);
  },
};
</script>
